import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslations, useDebounce } from "@jugl-web/utils";
import { AgentConfigData, useRestApiProvider } from "@jugl-web/rest-api";
import { AgentStyleSlider } from "./components/AgentStyleSlider";
import {
  EMOJI_USAGE_VALUES,
  RESPONSE_LENGTH_MAX,
  RESPONSE_LENGTH_MIN,
  TONE_VALUES,
  getDiscreteIndex,
  getDiscreteIndexByValue,
  getDiscreteValueByIndex,
  getSanitizedStyleConfig,
} from "./utils";

const SAVE_DEBOUNCE_MS = 500;

type StyleConfig = Pick<
  AgentConfigData,
  "tone" | "emoji_usage" | "response_length"
>;

const buildStyleKey = (config: StyleConfig) =>
  `${config.tone}|${config.emoji_usage}|${config.response_length}`;

export const AgentStylePanel: React.FC<{ entityId: string }> = ({
  entityId,
}) => {
  const { t } = useTranslations();
  const { integrationsApi } = useRestApiProvider();

  const { data } = integrationsApi.useGetAiAgentConfigQuery({
    entityId,
    meta: { silent: true },
  });
  const [updateAgentConfig] = integrationsApi.useUpdateAgentConfigMutation();

  const [style, setStyle] = useState<StyleConfig | null>(null);
  const [isInteractingWithSlider, setIsInteractingWithSlider] = useState(false);

  const debouncedStyle = useDebounce(style, SAVE_DEBOUNCE_MS);
  const lastSentKeyRef = useRef<string | null>(null);

  const updateStyle = useCallback((patch: Partial<StyleConfig>) => {
    setStyle((current) => {
      if (!current) return current;
      return { ...current, ...patch };
    });
  }, []);

  useEffect(() => {
    if (!data || style) return;
    const sanitized = getSanitizedStyleConfig(data.config);
    const hydratedStyle: StyleConfig = {
      tone: sanitized.tone,
      emoji_usage: sanitized.emoji_usage,
      response_length: Math.round(sanitized.response_length),
    };
    setStyle(hydratedStyle);
    lastSentKeyRef.current = buildStyleKey(hydratedStyle);

    const sanitizedKey = buildStyleKey(sanitized);
    const backendKey = buildStyleKey({
      tone: data.config.tone,
      emoji_usage: data.config.emoji_usage,
      response_length: data.config.response_length,
    });
    if (sanitizedKey === backendKey) return;
    if (lastSentKeyRef.current === sanitizedKey) return;
    lastSentKeyRef.current = sanitizedKey;
    updateAgentConfig({
      entityId,
      config: { ...data.config, ...sanitized },
    });
  }, [data, entityId, style, updateAgentConfig]);

  useEffect(() => {
    if (!data || !debouncedStyle || isInteractingWithSlider) return;

    const styleKey = buildStyleKey(debouncedStyle);
    if (styleKey === lastSentKeyRef.current) return;
    lastSentKeyRef.current = styleKey;
    updateAgentConfig({
      entityId,
      config: { ...data.config, ...debouncedStyle },
    });
  }, [
    data,
    debouncedStyle,
    entityId,
    isInteractingWithSlider,
    updateAgentConfig,
  ]);

  const conversationToneLevel = style
    ? getDiscreteValueByIndex(getDiscreteIndexByValue(style.tone, TONE_VALUES))
    : 0;
  const emojiUsageLevel = style
    ? getDiscreteValueByIndex(
        getDiscreteIndexByValue(style.emoji_usage, EMOJI_USAGE_VALUES)
      )
    : 0;
  const replyDetails = style
    ? Math.round(style.response_length)
    : RESPONSE_LENGTH_MIN;

  const controls = useMemo<
    {
      key: "conversationTone" | "emojiUsage" | "replyDetails";
      title: string;
      min: number;
      max: number;
      value: number;
      step: number;
      scaleLabels: [string, string, string];
      onChange: (value: number) => void;
      valueLabel?: string;
    }[]
  >(() => {
    const toneLabels: [string, string, string] = [
      t({
        id: "cx-ai-agents-page.agent-tone-business",
        defaultMessage: "Business",
      }),
      t({
        id: "cx-ai-agents-page.agent-tone-neutral",
        defaultMessage: "Neutral",
      }),
      t({
        id: "cx-ai-agents-page.agent-tone-creative",
        defaultMessage: "Creative",
      }),
    ];
    const emojiLabels: [string, string, string] = [
      t({
        id: "cx-ai-agents-page.emoji-usage-none",
        defaultMessage: "None",
      }),
      t({
        id: "cx-ai-agents-page.emoji-usage-average",
        defaultMessage: "Average",
      }),
      t({
        id: "cx-ai-agents-page.emoji-usage-many",
        defaultMessage: "Many",
      }),
    ];

    const responseLabels: [string, string, string] = [
      t({
        id: "cx-ai-agents-page.brief",
        defaultMessage: "Brief",
      }),
      t({
        id: "cx-ai-agents-page.balanced",
        defaultMessage: "Balanced",
      }),
      t({
        id: "cx-ai-agents-page.detailed",
        defaultMessage: "Detailed",
      }),
    ];

    return [
      {
        key: "conversationTone",
        title: t({
          id: "cx-ai-agents-page.conversation-tone",
          defaultMessage: "Conversation tone",
        }),
        min: 0,
        max: 10,
        value: conversationToneLevel,
        step: 5,
        onChange: (value) =>
          updateStyle({ tone: TONE_VALUES[getDiscreteIndex(value)] }),
        scaleLabels: toneLabels,
      },
      {
        key: "emojiUsage",
        title: t({
          id: "cx-ai-agents-page.emoji-usage",
          defaultMessage: "Emojis usage",
        }),
        min: 0,
        max: 10,
        value: emojiUsageLevel,
        step: 5,
        onChange: (value) =>
          updateStyle({
            emoji_usage: EMOJI_USAGE_VALUES[getDiscreteIndex(value)],
          }),
        scaleLabels: emojiLabels,
      },
      {
        key: "replyDetails",
        title: t({
          id: "cx-ai-agents-page.response-length",
          defaultMessage: "Response length",
        }),
        min: RESPONSE_LENGTH_MIN,
        max: RESPONSE_LENGTH_MAX,
        value: replyDetails,
        step: 10,
        valueLabel: t(
          {
            id: "cx-ai-agents-page.repsonse-length-words-count",
            defaultMessage: "{count} words",
          },
          { count: String(Math.round(replyDetails)) }
        ),
        onChange: (value) =>
          updateStyle({ response_length: Math.round(value / 10) * 10 }),
        scaleLabels: responseLabels,
      },
    ];
  }, [conversationToneLevel, emojiUsageLevel, replyDetails, t, updateStyle]);

  if (!data) return null;
  return (
    <div className="flex flex-col gap-10">
      {controls.map((control) => (
        <div key={control.key} className="flex flex-col">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between font-secondary text-sm leading-[21px] text-dark">
              <span>{control.title}</span>
              <span>{control.valueLabel}</span>
            </div>
            <AgentStyleSlider
              min={control.min}
              max={control.max}
              step={control.step}
              value={control.value}
              onChange={control.onChange}
              onInteractionStart={() => setIsInteractingWithSlider(true)}
              onInteractionEnd={() => setIsInteractingWithSlider(false)}
              width="100%"
            />
          </div>
          <div className="flex items-center justify-between font-secondary text-xs leading-2 text-grey-900">
            <span>{control.scaleLabels[0]}</span>
            <span>{control.scaleLabels[1]}</span>
            <span>{control.scaleLabels[2]}</span>
          </div>
        </div>
      ))}
    </div>
  );
};
